/**
 * World layout: the tree grows along +Y. The camera starts above the
 * canopy and descends past the artworks to the roots.
 */
export const WORLD = {
  canopyTop: 30,
  rootsBottom: -7,

  // Vertical band where works of each year get placed (top -> bottom,
  // reverse chronological). Computed dynamically from the works list,
  // but these bounds frame the usable trunk region.
  worksTop: 27.5,
  worksBottom: 2.5,

  // Radius of the helix on which artworks hang.
  workRadius: 4.6,
  // Camera orbit radius.
  cameraRadius: 12.5,
  // How far the helix advances per artwork (radians).
  helixStep: 0.85
};

export const PALETTE = {
  // Height gradient stops, top (1) -> bottom (0).
  stops: [
    { t: 1.0, color: [0.62, 1.0, 0.34] }, // lime canopy
    { t: 0.78, color: [0.36, 0.91, 1.0] }, // cyan
    { t: 0.52, color: [0.54, 0.44, 1.0] }, // violet
    { t: 0.28, color: [1.0, 0.36, 0.82] }, // magenta
    { t: 0.0, color: [1.0, 0.71, 0.34] } // amber roots
  ],
  background: 0x04050c,
  accent: 0x9dff57
};

/** Pick the palette colour for a normalized height (0 bottom, 1 top). */
export function paletteAt(t) {
  const stops = PALETTE.stops;
  const x = Math.max(0, Math.min(1, t));
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (x <= a.t && x >= b.t) {
      const f = (a.t - x) / Math.max(1e-5, a.t - b.t);
      return [
        a.color[0] + (b.color[0] - a.color[0]) * f,
        a.color[1] + (b.color[1] - a.color[1]) * f,
        a.color[2] + (b.color[2] - a.color[2]) * f
      ];
    }
  }
  return stops[stops.length - 1].color.slice();
}

/** Normalize a world y into 0..1 for palette lookups. */
export function heightT(y) {
  return (y - WORLD.rootsBottom) / (WORLD.canopyTop - WORLD.rootsBottom);
}

export function detectTier() {
  const coarse = window.matchMedia?.("(pointer: coarse)")?.matches;
  const small = Math.min(window.innerWidth, window.innerHeight) < 760;
  const lowMem = (navigator.deviceMemory || 8) < 4;
  const mobile = (coarse && small) || lowMem || window.innerWidth < 700;

  return mobile
    ? {
        name: "mobile",
        branchParticles: 15000,
        motes: 700,
        orbitersPerWork: 60,
        pixelRatioCap: 1.5,
        bloom: true,
        filmPass: false
      }
    : {
        name: "desktop",
        branchParticles: 60000,
        motes: 2200,
        orbitersPerWork: 130,
        pixelRatioCap: 2,
        bloom: true,
        filmPass: true
      };
}

export function prefersReducedMotion() {
  return !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
}

export function isWebGLAvailable() {
  try {
    const canvas = document.createElement("canvas");
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext("webgl2") || canvas.getContext("webgl"))
    );
  } catch {
    return false;
  }
}
